import React from 'react';
import { Box, Paper, Typography, Avatar, useTheme } from '@mui/material';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

interface TestimonialCardProps {
  quote: string;
  author: string;
  position: string;
  avatar: string;
  index: number;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ 
  quote, 
  author, 
  position, 
  avatar,
  index 
}) => {
  const theme = useTheme();
  
  // Generate a color based on the index
  const getAccentColor = (idx: number) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      '#00C49F',
      '#FF8042',
      '#8884d8',
      '#82ca9d',
    ];
    return colors[idx % colors.length];
  };
  
  const accentColor = getAccentColor(index);
  
  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
        backdropFilter: 'blur(10px)',
        backgroundColor: 'white',
        border: 'none',
        borderBottom: `5px solid ${accentColor}`,
        '&:hover': {
          transform: 'translateY(-10px)',
          boxShadow: `0 20px 40px rgba(${parseInt(accentColor.slice(1, 3), 16)}, ${parseInt(accentColor.slice(3, 5), 16)}, ${parseInt(accentColor.slice(5, 7), 16)}, 0.2)`,
          '& .quote-icon': {
            transform: 'rotate(-10deg) scale(1.2)',
            opacity: 0.2,
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '5px',
          background: accentColor,
          zIndex: 2
        }
      }}
    >
      <Box
        className="quote-icon"
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          color: accentColor,
          opacity: 0.1,
          transform: 'rotate(0deg) scale(1)',
          transition: 'all 0.5s ease',
        }}
      >
        <FormatQuoteIcon sx={{ fontSize: 60 }} />
      </Box>
      
      <Typography
        variant="body1"
        sx={{
          mb: 4,
          fontStyle: 'italic',
          color: 'text.secondary',
          position: 'relative',
          zIndex: 1,
          lineHeight: 1.7,
          flex: 1
        }}
      >
        "{quote}"
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
        <Avatar
          src={avatar}
          sx={{
            width: 56,
            height: 56,
            mr: 2,
            border: `3px solid ${accentColor}`,
            boxShadow: `0 0 0 2px white`,
          }}
        />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {author}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {position}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default TestimonialCard; 